import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Bell } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const NotificationBell = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000); // Poll every 30 seconds
      return () => clearInterval(interval);
    }
  }, [user]);

  const fetchNotifications = async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        api().get('/notifications'),
        api().get('/notifications/unread-count')
      ]);
      setNotifications(notifRes.data.slice(0, 5));
      setUnreadCount(countRes.data.count);
    } catch (err) {
      console.error('Failed to fetch notifications', err);
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      await api().put(`/notifications/${notification.id}/read`);
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      // Route based on notification type
      if (notification.type === 'merch_order') {
        navigate('/admin/merchandise?tab=orders');
      } else if (notification.type === 'broadcast') {
        // Broadcast messages - just mark as read, no redirect needed
        // Could optionally show full message in a modal
      } else if (notification.type === 'new_message' && notification.conversation_id) {
        navigate(`/messages/${notification.conversation_id}`);
      } else if (notification.conversation_id) {
        navigate(`/messages/${notification.conversation_id}`);
      } else if (notification.listing_id) {
        navigate(`/listing/${notification.listing_id}`);
      } else if (notification.type === 'trial_expiring') {
        navigate('/subscription');
      } else if (notification.type === 'subscription') {
        navigate('/subscription');
      } else if (notification.type === 'order_update') {
        navigate('/orders');
      }
    } catch (err) {
      console.error('Failed to mark notification as read', err);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="relative p-2 text-stone-600 hover:text-teal-600 transition-colors" data-testid="notification-bell">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="notification-badge" data-testid="notification-count">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="px-3 py-2 border-b border-stone-100">
          <h3 className="font-semibold text-stone-900">Notifications</h3>
        </div>
        {notifications.length === 0 ? (
          <div className="px-3 py-8 text-center text-stone-500">
            No notifications yet
          </div>
        ) : (
          notifications.map((notif) => (
            <DropdownMenuItem
              key={notif.id}
              onClick={() => handleNotificationClick(notif)}
              className={`cursor-pointer ${!notif.read ? 'bg-teal-50' : ''}`}
              data-testid={`notification-item-${notif.id}`}
            >
              <div className="flex-1">
                <p className={`text-sm ${!notif.read ? 'font-semibold' : ''}`}>
                  {notif.title}
                </p>
                <p className="text-xs text-stone-500 mt-0.5">{notif.message}</p>
                <p className="text-xs text-stone-400 mt-1">{formatTime(notif.created_at)}</p>
              </div>
              {!notif.read && (
                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-sky-500 to-teal-500 flex-shrink-0" />
              )}
            </DropdownMenuItem>
          ))
        )}
        {notifications.length > 0 && (
          <div className="px-3 py-2 border-t border-stone-100 flex justify-between items-center">
            <button
              onClick={async () => {
                try {
                  await api().put('/notifications/read-all');
                  setUnreadCount(0);
                  setNotifications(notifications.map(n => ({ ...n, read: true })));
                } catch (err) {
                  console.error('Failed to mark all as read', err);
                }
              }}
              className="text-sm text-stone-500 hover:text-stone-700"
            >
              Mark all as read
            </button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
